import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Cpu, Zap, Sparkles, Gem, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MODEL_META = {
  haiku: { label: "Haiku", hint: "Claude · fast, low cost", Icon: Zap },
  sonnet: { label: "Sonnet", hint: "Claude · sharper reasoning", Icon: Sparkles },
  "gemini-flash": { label: "Gemini Flash", hint: "Gemini 3.5 Flash · fast", Icon: Wand2 },
  "gemini-pro": { label: "Gemini Pro", hint: "Gemini 3.1 Pro · deepest reasoning", Icon: Gem },
};

export function ModelSwitch() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => { api.get("/settings/models").then(r => setData(r.data)).catch(err => console.error("[ARIA]", err)); }, []);

  const choose = async (feature, model) => {
    if (!data || data.prefs[feature] === model) return;
    setSaving(feature);
    try {
      const { data: res } = await api.put("/settings/models", { [feature]: model });
      setData(d => ({ ...d, prefs: res.prefs }));
      toast.success(`${data.features[feature]} → ${MODEL_META[model].label}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(null); }
  };

  const setAll = async (model) => {
    setSaving("all");
    try {
      const patch = Object.fromEntries(Object.keys(data.features).map(f => [f, model]));
      const { data: res } = await api.put("/settings/models", patch);
      setData(d => ({ ...d, prefs: res.prefs }));
      toast.success(`Everything on ${MODEL_META[model].label}`);
    } catch { toast.error("Could not save"); }
    finally { setSaving(null); }
  };

  if (!data) return null;
  const options = Object.keys(MODEL_META).filter(m => m in (data.models || {}));

  return (
    <div className="aria-card p-5 mb-6" data-testid="model-switch">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2"><Cpu size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">Model per feature</span></div>
        <span className="aria-pill text-[10px]" data-testid="model-provider-pill">Claude ({data.provider === "anthropic" ? "your key" : "Emergent key"}) + Gemini (your key)</span>
      </div>
      <p className="text-sm text-[var(--aria-text-muted)] mb-4">Pick the engine behind each feature. Claude Haiku & Gemini Flash are quick and cheap; Claude Sonnet & Gemini Pro think harder — worth it where a sloppy judgement costs you (interview grading, debriefs). Gemini uses your own key; if it's ever unavailable, ARIA falls back to Claude automatically.</p>
      <div className="flex gap-2 mb-4 text-xs flex-wrap">
        {options.map(m => (
          <button key={m} onClick={() => setAll(m)} disabled={!!saving} data-testid={`model-all-${m}`}
            className="px-3 py-1.5 rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-primary)] disabled:opacity-50 inline-flex items-center gap-1">
            {(() => { const I = MODEL_META[m].Icon; return <I size={12} />; })()} All {MODEL_META[m].label}
          </button>
        ))}
        {saving && <Loader2 size={14} className="animate-spin text-[var(--aria-text-muted)] self-center" />}
      </div>
      <div className="space-y-1.5" data-testid="model-feature-list">
        {Object.entries(data.features).map(([feature, label]) => (
          <div key={feature} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[var(--aria-surface-2)] text-sm" data-testid={`model-row-${feature}`}>
            <span className="truncate">{label}</span>
            <div className="flex rounded-md border border-[var(--aria-border)] overflow-hidden shrink-0 flex-wrap justify-end">
              {options.map((m) => {
                const meta = MODEL_META[m];
                const active = data.prefs[feature] === m;
                return (
                  <button key={m} onClick={() => choose(feature, m)} disabled={saving === feature} title={meta.hint}
                    data-testid={`model-${feature}-${m}`} aria-pressed={active}
                    className={`px-2.5 py-1 text-xs inline-flex items-center gap-1 transition-colors ${active ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
                    <meta.Icon size={11} /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
